"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import {
  getScrollPosition,
  saveScrollPosition,
} from "@/lib/offlinePersistence";

/**
 * StateRestorer — restores the user's scroll position on every route change
 * (and on reload / browser reopen) so the app returns the user to exactly
 * where they left off. The current *route* is already preserved by the URL in
 * the Next.js_App Router; the missing piece was the scroll offset and the
 * fetched content (handled separately by the IndexedDB + service-worker
 * caches). Mounted once near the app root.
 */
export default function StateRestorer() {
  const pathname = usePathname();

  // Persist scroll position for the active route as the user scrolls.
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        saveScrollPosition(window.location.pathname, window.scrollY);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);

  // Restore scroll position after the route (and its content) has settled.
  useEffect(() => {
    const route = window.location.pathname;
    const restore = () => window.scrollTo(0, getScrollPosition(route));
    // First, immediately (covers static content on reload). Then again after a
    // short delay so async-rendered (API-backed) content has painted.
    restore();
    const t = window.setTimeout(restore, 300);
    return () => window.clearTimeout(t);
  }, [pathname]);

  return null;
}
